import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Check, Undo2, Ban, Wallet, Receipt } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { VentaEnLista, EstadoDeVenta } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle, type Entidad } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { enRango, opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, fecha, haceCuanto } from '@/lib/formato-tienda';
import { DetalleDeVenta, DetalleDeCliente, DetalleDeProducto } from '@/components/datos/detalles';
import { NuevaVenta } from '@/components/tienda/NuevaVenta';
import { Comprobante } from '@/components/tienda/Comprobante';

/**
 * VENTAS — de lista de tarjetas a sistema de facturación.
 *
 * ═══ LOS TRES ESTADOS, Y SON DISTINTOS ═══
 *
 *  · **Borrador**: armado, no descuenta nada. Se corrige o se abandona.
 *  · **Confirmada**: descontó inventario y congeló precio y costo.
 *  · **Cobrada**: entró la plata.
 *
 * Confirmada y cobrada no son lo mismo, y juntarlas sería el error más caro de
 * esta pantalla: una venta contra entrega sale con el mensajero —y por eso
 * descuenta— pero la plata entra cuando él vuelve. Si fueran un solo estado,
 * «vendido» y «lo que tengo en la mano» serían el mismo número.
 */

const VISTAS = [
  { id: 'todas', etiqueta: 'Todas' },
  { id: 'borradores', etiqueta: 'Borradores', ayuda: 'Armadas, sin descontar todavía' },
  { id: 'por-cobrar', etiqueta: 'Por cobrar', ayuda: 'Confirmadas que aún no se han pagado' },
  { id: 'confirmadas', etiqueta: 'Confirmadas' },
  { id: 'anuladas', etiqueta: 'Anuladas' },
] as const;

const ESTADO: Record<
  EstadoDeVenta,
  { etiqueta: string; variant: 'default' | 'success' | 'danger' }
> = {
  BORRADOR: { etiqueta: 'Borrador', variant: 'default' },
  CONFIRMADA: { etiqueta: 'Confirmada', variant: 'success' },
  ANULADA: { etiqueta: 'Anulada', variant: 'danger' },
};

export default function Ventas() {
  return (
    <ConDetalle render={(e) => <Detalle entidad={e} />}>
      <Contenido />
    </ConDetalle>
  );
}

function Detalle({ entidad }: { entidad: Entidad }) {
  if (entidad.tipo === 'venta') return <DetalleDeVenta id={entidad.id} />;
  if (entidad.tipo === 'cliente') return <DetalleDeCliente id={entidad.id} />;
  if (entidad.tipo === 'producto') return <DetalleDeProducto id={entidad.id} />;
  return <p className="text-sm text-slate-500">Nada que mostrar.</p>;
}

function Contenido() {
  const abrir = useAbrirDetalle();
  const toast = useToast();
  const qc = useQueryClient();
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { vista: 'todas', buscar: '', nueva: '', pago: '', desde: '', hasta: '' },
    { vista: VISTAS.map((v) => v.id) },
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ventas', 'todas'],
    queryFn: () => tienda.listarVentas({ limit: 200 }),
  });
  const ventas = useMemo(() => data ?? [], [data]);

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['ventas'] });
    void qc.invalidateQueries({ queryKey: ['venta'] });
    void qc.invalidateQueries({ queryKey: ['inventario'] });
    void qc.invalidateQueries({ queryKey: ['productos'] });
    void qc.invalidateQueries({ queryKey: ['tienda'] });
  };

  const confirmar = useMutation({
    mutationFn: (id: string) => tienda.confirmarVenta(id),
    onSuccess: () => {
      toast.success('Venta confirmada. La existencia ya bajó.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo confirmar'),
  });

  const cobrar = useMutation({
    mutationFn: (id: string) => tienda.marcarCobrada(id, true),
    onSuccess: () => {
      toast.success('Marcada como cobrada.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const [comprobante, setComprobante] = useState<VentaEnLista | null>(null);
  const [anulando, setAnulando] = useState<VentaEnLista | null>(null);
  const [motivo, setMotivo] = useState('');
  const anular = useMutation({
    mutationFn: () => tienda.anularVenta(anulando!.id, motivo.trim()),
    onSuccess: () => {
      toast.success('Venta anulada. La existencia volvió.');
      setAnulando(null);
      setMotivo('');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo anular'),
  });

  const confirmadas = ventas.filter((v) => v.estado === 'CONFIRMADA');
  const porCobrar = confirmadas.filter((v) => !v.cobrado);
  const borradores = ventas.filter((v) => v.estado === 'BORRADOR');

  // NETO DE DEVOLUCIONES. `total` es lo que se vendió ese día y no baja al
  // devolver — el libro es la verdad. Sumarlo tal cual daba un «vendido» y un
  // «por cobrar» por encima de los de Finanzas, en la misma aplicación.
  const neto = (v: VentaEnLista) => v.porCobrar ?? v.total;
  const vendido = confirmadas.reduce((a, v) => a + neto(v), 0);
  const devuelto = confirmadas.reduce((a, v) => a + (v.devuelto ?? 0), 0);

  const filtradas = useMemo(() => {
    let salida = ventas;
    const v = filtros.vista;
    if (v === 'borradores') salida = salida.filter((x) => x.estado === 'BORRADOR');
    if (v === 'confirmadas') salida = salida.filter((x) => x.estado === 'CONFIRMADA');
    if (v === 'anuladas') salida = salida.filter((x) => x.estado === 'ANULADA');
    if (v === 'por-cobrar') salida = salida.filter((x) => x.estado === 'CONFIRMADA' && !x.cobrado);
    if (filtros.pago) salida = salida.filter((x) => (x.formaDePago ?? '') === filtros.pago);
    salida = salida.filter((x) => enRango(x.createdAt, filtros.desde, filtros.hasta));

    const q = filtros.buscar.trim().toLowerCase();
    if (q) {
      salida = salida.filter(
        (x) =>
          x.numero.toLowerCase().includes(q) ||
          (x.cliente?.nombre ?? '').toLowerCase().includes(q) ||
          x.lineas.some((l) =>
            `${l.productVariant?.product?.name ?? ''} ${l.productVariant?.product?.sku ?? ''}`
              .toLowerCase()
              .includes(q),
          ),
      );
    }
    return salida;
  }, [ventas, filtros.vista, filtros.buscar, filtros.pago, filtros.desde, filtros.hasta]);

  // SUPERFILTRO (Josué, 14-oct): las pestañas de antes son la faceta «Estado»; se suman el pago y la fecha.
  const cuentaDe = (id: string) =>
    id === 'borradores' ? borradores.length : id === 'por-cobrar' ? porCobrar.length : id === 'confirmadas' ? confirmadas.length : ventas.filter((x) => x.estado === 'ANULADA').length;
  const facetas: Faceta[] = [
    { clave: 'vista', titulo: 'Estado', todas: 'Todas', opciones: VISTAS.filter((v) => v.id !== 'todas').map((v) => ({ valor: v.id, etiqueta: v.etiqueta, cuenta: cuentaDe(v.id) })) },
    { clave: 'pago', titulo: 'Forma de pago', opciones: opcionesDe(ventas, (v) => v.formaDePago) },
    { tipo: 'fechas', clave: 'fecha', titulo: 'Fecha', desde: 'desde', hasta: 'hasta', hastaHoy: true },
  ];

  const columnas: Columna<VentaEnLista>[] = [
    {
      id: 'numero',
      titulo: 'N°',
      fija: true,
      ancho: 'w-28',
      valor: (v) => v.numero,
      celda: (v) => (
        <EntityLink
          tipo="venta"
          id={v.id}
          etiqueta={v.numero}
          detalle={fecha(v.createdAt)}
          mono
          sinIcono
        />
      ),
    },
    {
      id: 'fecha',
      titulo: 'Fecha',
      valor: (v) => v.createdAt,
      celda: (v) => (
        <span title={haceCuanto(v.createdAt)} className="text-slate-600 dark:text-slate-300">
          {fecha(v.createdAt)}
        </span>
      ),
    },
    {
      id: 'cliente',
      titulo: 'Cliente',
      valor: (v) => v.cliente?.nombre ?? '',
      celda: (v) =>
        v.cliente ? (
          <EntityLink tipo="cliente" id={v.cliente.id} etiqueta={v.cliente.nombre} />
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      id: 'articulos',
      titulo: 'Artículos',
      valor: (v) => v.lineas.reduce((a, l) => a + l.cantidad, 0),
      celda: (v) => {
        const piezas = v.lineas.reduce((a, l) => a + l.cantidad, 0);
        const primera = v.lineas[0]?.productVariant?.product?.name;
        return (
          <span className="text-slate-600 dark:text-slate-300">
            {piezas} pieza{piezas === 1 ? '' : 's'}
            {primera && v.lineas.length === 1 && (
              <span className="ml-1 text-xs text-slate-400">· {primera}</span>
            )}
            {v.lineas.length > 1 && (
              <span className="ml-1 text-xs text-slate-400">· {v.lineas.length} líneas</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'pago',
      titulo: 'Pago',
      valor: (v) => v.formaDePago ?? '',
      celda: (v) => (
        <span className="text-slate-600 dark:text-slate-300">{v.formaDePago ?? '—'}</span>
      ),
    },
    {
      id: 'cobro',
      titulo: 'Cobro',
      valor: (v) => (v.estado !== 'CONFIRMADA' ? '' : v.cobrado ? 'cobrada' : 'pendiente'),
      celda: (v) => {
        if (v.estado !== 'CONFIRMADA') return <span className="text-slate-400">—</span>;
        return v.cobrado ? (
          <Badge variant="success">Cobrada</Badge>
        ) : (
          <Badge variant="warning">Por cobrar</Badge>
        );
      },
    },
    {
      id: 'estado',
      titulo: 'Estado',
      valor: (v) => v.estado,
      celda: (v) => <Badge variant={ESTADO[v.estado].variant}>{ESTADO[v.estado].etiqueta}</Badge>,
    },
    {
      id: 'total',
      titulo: 'Total',
      numerica: true,
      valor: (v) => v.total,
      celda: (v) => {
        if (v.estado === 'BORRADOR') return <span className="text-slate-400">—</span>;
        const vuelto = v.devuelto ?? 0;
        // Se enseñan los dos: lo que se facturó y lo que queda en pie. Solo el
        // neto escondería que hubo una devolución; solo el bruto mentiría
        // sobre lo que se cobra.
        return vuelto > 0 ? (
          <span title={`${dinero(vuelto)} devuelto`}>
            <span className="text-slate-400 line-through">{dinero(v.total)}</span>{' '}
            {dinero(v.total - vuelto)}
          </span>
        ) : (
          dinero(v.total)
        );
      },
      total: (fs) =>
        dinero(fs.filter((v) => v.estado === 'CONFIRMADA').reduce((a, v) => a + neto(v), 0)),
    },
    {
      id: 'envio',
      titulo: 'Envío',
      numerica: true,
      ocultaPorDefecto: true,
      valor: (v) => v.envio,
      celda: (v) => (v.envio > 0 ? dinero(v.envio) : <span className="text-slate-400">—</span>),
    },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Ventas"
        subtitulo="Confirmar descuenta la existencia. Cobrar es otro paso: la plata entra cuando vuelve el mensajero."
        acciones={
          <Button onClick={() => poner('nueva', '1')}>
            <Plus className="h-4 w-4" />
            Nueva venta
          </Button>
        }
        kpis={
          <>
            <Kpi
              titulo="Vendido"
              valor={dinero(vendido)}
              pie={
                devuelto > 0
                  ? `${confirmadas.length} ventas · ${dinero(devuelto)} devuelto, ya restado`
                  : `${confirmadas.length} venta${confirmadas.length === 1 ? '' : 's'}`
              }
              onClick={() => poner('vista', 'confirmadas')}
            />
            <Kpi
              titulo="Ticket promedio"
              valor={dinero(confirmadas.length ? Math.round(vendido / confirmadas.length) : 0)}
            />
            <Kpi
              titulo="Por cobrar"
              valor={dinero(porCobrar.reduce((a, v) => a + neto(v), 0))}
              pie={`${porCobrar.length} contra entrega`}
              tono={porCobrar.length > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'por-cobrar')}
            />
            <Kpi
              titulo="Borradores"
              valor={numero(borradores.length)}
              pie="esperan confirmación"
              onClick={() => poner('vista', 'borradores')}
            />
          </>
        }
      />

      <DataTable
        filas={filtradas}
        columnas={columnas}
        idDe={(v) => v.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholderBuscar="Busca por número, cliente o producto…"
        facetas={facetas}
        valoresDeFacetas={{ ...filtros, vista: filtros.vista === 'todas' ? undefined : filtros.vista }}
        onFacetas={ponerVarios}
        onLimpiarFiltros={() => ponerVarios(sinFiltros(facetas))}
        resultados={textoDeResultados(filtradas.length, ventas.length, ['venta', 'ventas'])}
        memoria="ventas"
        exportarComo="ventas"
        onAbrirFila={(v) =>
          abrir?.({ tipo: 'venta', id: v.id, etiqueta: v.numero, detalle: fecha(v.createdAt) })
        }
        accionesDeFila={[
          {
            etiqueta: 'Confirmar',
            icono: <Check className="h-3.5 w-3.5" />,
            ocultaSi: (v) => v.estado !== 'BORRADOR',
            onClick: (v) => confirmar.mutate(v.id),
          },
          {
            etiqueta: 'Comprobante',
            icono: <Receipt className="h-3.5 w-3.5" />,
            ocultaSi: (v) => v.estado !== 'CONFIRMADA',
            onClick: (v) => setComprobante(v),
          },
          {
            etiqueta: 'Marcar cobrada',
            icono: <Wallet className="h-3.5 w-3.5" />,
            ocultaSi: (v) => v.estado !== 'CONFIRMADA' || v.cobrado,
            onClick: (v) => cobrar.mutate(v.id),
          },
          {
            etiqueta: 'Devolver',
            icono: <Undo2 className="h-3.5 w-3.5" />,
            ocultaSi: (v) => v.estado !== 'CONFIRMADA',
            onClick: (v) =>
              abrir?.({ tipo: 'venta', id: v.id, etiqueta: v.numero, detalle: 'devolver' }),
          },
          {
            etiqueta: 'Anular todo',
            icono: <Ban className="h-3.5 w-3.5" />,
            peligrosa: true,
            ocultaSi: (v) => v.estado !== 'CONFIRMADA',
            onClick: (v) => setAnulando(v),
          },
        ]}
        accionesEnLote={[
          {
            etiqueta: 'Confirmar',
            icono: <Check className="h-3.5 w-3.5" />,
            onClick: (vs) =>
              vs.filter((v) => v.estado === 'BORRADOR').forEach((v) => confirmar.mutate(v.id)),
          },
          {
            etiqueta: 'Marcar cobradas',
            icono: <Wallet className="h-3.5 w-3.5" />,
            onClick: (vs) =>
              vs
                .filter((v) => v.estado === 'CONFIRMADA' && !v.cobrado)
                .forEach((v) => cobrar.mutate(v.id)),
          },
        ]}
        vacio={{
          titulo: filtros.buscar ? `Nada con "${filtros.buscar}"` : 'Todavía no hay ventas',
          descripcion: 'Cuando registres la primera, la existencia se va a descontar sola.',
          accion: <Button onClick={() => poner('nueva', '1')}>Nueva venta</Button>,
        }}
      />

      {filtros.nueva === '1' && (
        <NuevaVenta
          onCerrar={() => poner('nueva', undefined)}
          onCreada={() => {
            poner('nueva', undefined);
            invalidar();
          }}
        />
      )}

      {comprobante && (
        <Comprobante ventaId={comprobante.id} onCerrar={() => setComprobante(null)} />
      )}

      <Modal
        open={anulando != null}
        onClose={() => setAnulando(null)}
        title={`Anular ${anulando?.numero ?? ''}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Van a volver al inventario{' '}
            <strong>
              {anulando?.lineas.reduce((a, l) => a + l.cantidad, 0) ?? 0} pieza
              {(anulando?.lineas.reduce((a, l) => a + l.cantidad, 0) ?? 0) === 1 ? '' : 's'}
            </strong>
            . La venta no se borra: queda anulada, y el libro conserva la salida y la entrada.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Motivo <span className="text-red-600">*</span>
            </label>
            <Input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="La clienta se arrepintió"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAnulando(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => anular.mutate()}
              disabled={!motivo.trim() || anular.isPending}
            >
              {anular.isPending ? 'Anulando…' : 'Anular venta'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
